// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';
import { UserWorkspace } from './getUserWorkspace';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/Workspace/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {};

// ============= Types =============

export interface GetWorkspaceProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Fetches workspace information by workspace ID
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace identifier
 * @returns Promise with API response containing workspace data or error
 */
export const getWorkspace = async ({
  getAccessToken,
  workspaceId,
}: GetWorkspaceProps): Promise<UserWorkspace> => {
  try {
    console.log('🔄 Fetching workspace...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Workspace fetched successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error fetching workspace:', error);
    throw error;
  }
};

